import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Page, Layout, Text, Card, RadioButtonGroup, InterfaceNameValue, Input, Button, Stack, Modal, Toast } from "@tiendanube/components";
import IFrameSkeleton from "./components/IFrameSkeleton";
import InputDelayed from "./components/InputDelayed";

import { SIZE, heights, widths, contentClipboard, sanitizeColor } from "./utils";
import { useStore } from "../../StoreContext";
import { copyToClipboard } from "../../libs/nexo/helpers";
import nexo from "../../nexoClient";


function ProductCreateWidget() {

    const { t } = useTranslation();
    const { goBack } = useHistory();
    const { id } = useParams<{ id: string }>();
    const { store } = useStore();

    const backToProducts = () => goBack();

    // Form

    const initialValues = {
        size: SIZE.SMALL,
        colorText: '#2C3357',
        colorBackgroundButton: '#2C3357',
        colorTextButton: '#FFFFFF',
        textButton: t('createWidget.button.defaultValue')
    }

    const [values, setValues] = useState(initialValues);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [isSubmiting, setIsSubmiting] = useState(false);
    const [url, setUrl] = useState('');


    const inputAppearance = error && isSubmiting ? 'validation_error' : 'default';
    const helpText =
        error && isSubmiting ? t('createWidget.button.error') : '';

    const { size, textButton, colorText, colorBackgroundButton, colorTextButton } = values;

    const poweredLabel = t('createWidget.poweredBy.label');
    const poweredText = t('createWidget.poweredBy.text');
    const poweredUrl = t('createWidget.poweredBy.url');

    const colorTextStz = sanitizeColor(colorText);
    const colorBackgroundButtonStz = sanitizeColor(colorBackgroundButton);
    const colorTextButtonStz = sanitizeColor(colorTextButton);

    const baseUrl = process.env.REACT_APP_API_BFF;

    useEffect(() => {
        if ((colorText + colorBackgroundButton + colorTextButton).length === 21) {
            setLoading(true);
            setUrl(`${baseUrl}/widget/render?storeId=${store.id}&productId=${id}&size=${size}&colorText=${colorTextStz}&colorBackgroundButton=${colorBackgroundButtonStz}&colorTextButton=${colorTextButtonStz}&textButton=${textButton}&poweredLabel=${poweredLabel}&poweredText=${poweredText}&poweredUrl=${poweredUrl}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size, colorTextStz, colorBackgroundButtonStz, colorTextButtonStz, textButton]);

    const optionsSize = [
        {
            label: t('createWidget.size.small'),
            value: SIZE.SMALL,
        }, {
            label: t('createWidget.size.medium'),
            value: SIZE.MEDIUM
        }, {
            label: t('createWidget.size.large'),
            value: SIZE.LARGE
        }
    ]

    const createLabel = t('createWidget.create');

    const isValidForm = (): boolean => {
        return !!values.textButton.replace(/ /g, '');
    };

    const handleOnBlur = () => {
        if (isValidForm() && isSubmiting) setError(false);
    };

    const handleChange = (event: InterfaceNameValue) => {
        setValues((old) => ({ ...old, [event.name]: event.value }));
    };

    const isValidColor = (value: string) => {
        const test = value.match(/^#([A-Fa-f0-9]{0,6})$/);
        return (test && test[0] === test?.input) || false;
    }

    const handleChangeColor = (event: InterfaceNameValue) => {
        if (isValidColor(event.value)) {
        setValues((old) => ({ ...old, [event.name]: event.value }));
        }
    };

    // Modal 
    const [show, setShow] = useState(false);
    const titleModal = t('createWidget.modal.title')
    const content1 = t('createWidget.modal.content1');
    const content2 = t('createWidget.modal.content2');
    const copyCode = t('createWidget.modal.copyCode');

    const openModal = () => {
        setIsSubmiting(true);
        if (!isValidForm()) {
            setError(true);
            return;
        }
        setShow(true);
    }
    const closeModal = () => setShow(false);

    const handleClickModal = async () => {
        copyToClipboard(nexo, contentClipboard(url, `${widths[values.size] + 16}px`, `${heights[values.size]}px`));
        closeModal();
        showToast();
    }

    // Toast
    const [activeToast, setActiveToast] = React.useState(false);
    const labelToast = t('createWidget.toast');
    const showToast = () => setActiveToast(true);
    const hideToast = () => setActiveToast(false);

    return <Page headerNavigation={{ onClick: backToProducts }}
        title={t('createWidget.title')}
        subtitle={t('createWidget.subTitle')}> 
        <Layout>
            <Layout.Section>
                <Card title={t('createWidget.customize')} >
                    <RadioButtonGroup
                        label={t('createWidget.size.title')}
                        name="size"
                        options={optionsSize}
                        onChange={handleChange} value={values.size}
                    />
                    <Input
                        type="color"
                        name="colorText"
                        label={t('createWidget.color.text')}
                        value={values.colorText}
                        maxLength={7}
                        onChange={handleChangeColor}
                    />
                    <Input
                        type="color"
                        name="colorBackgroundButton"
                        label={t('createWidget.color.backgroundButton')}
                        value={values.colorBackgroundButton}
                        maxLength={7}
                        onChange={handleChangeColor} />
                    <Input
                        type="color"
                        name="colorTextButton"
                        label={t('createWidget.color.textButton')}
                        value={values.colorTextButton}
                        maxLength={7}
                        onChange={handleChangeColor}
                    />
                    <InputDelayed
                        name="textButton"
                        appearance={inputAppearance}
                        label={t('createWidget.button.text')}
                        value={values.textButton}
                        helpText={helpText}
                        onChange={handleChange}
                        onBlur={handleOnBlur}
                    />
                </Card>
                <Card title={t('createWidget.preview')} >
                    <Stack justify="center" align="center">
                        <Stack.Item>
                            {loading && <IFrameSkeleton height={heights[values.size]} width={widths[values.size]} />}
                            <iframe title="widget"
                                style={{ display: loading ? 'none' : 'inherit', pointerEvents: 'none' }}
                                src={url}
                                width={`${widths[values.size] + 16}px`}
                                height={`${heights[values.size]}px`}
                                onLoad={() => { setLoading(false); }}
                            />
                        </Stack.Item>
                    </Stack>
                </Card>
                <br />
                <Stack spacing="base" justify="flex-end">
                    <Stack.Item >
                        <Button appearance="primary" onClick={openModal}>{createLabel}</Button>
                    </Stack.Item>
                </Stack>
                <br />
                <Modal show={show}
                    title={titleModal}
                    primaryAction={{
                        appearance: "primary",
                        children: copyCode,
                        onClick: handleClickModal
                    }}
                    onDismiss={closeModal}>
                    <Text>{content1}</Text>
                    <Text>{content2}</Text>
                </Modal>
                {
                    activeToast &&
                    <Toast
                        label={labelToast}
                        appearance="success"
                        onClose={hideToast}
                    />
                }
            </Layout.Section>
        </Layout>
    </Page >
}

export default ProductCreateWidget;